import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdSearch, MdFileDownload } from 'react-icons/md';
import '../master/MasterData.css';

interface Employee {
  employeeId: number;
  employeeCode: string;
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  department: string;
  position: string;
  tin: string;
  sssNumber: string;
  philhealthNumber: string;
  pagibigNumber: string;
  basicSalary: number;
  email: string;
  mobile: string;
  isActive: string;
}

interface AttendanceRecord {
  recordId: number;
  employeeCode: string;
  employeeName: string;
  date: string;
  timeIn: string;
  timeOut: string;
  regularHours: number;
  overtimeHours: number;
  lateMinutes: number;
  undertimeMinutes: number;
  leaveType: 'NONE' | 'SICK_LEAVE' | 'VACATION_LEAVE' | 'EMERGENCY_LEAVE' | 'ABSENT';
  status: 'PRESENT' | 'LATE' | 'UNDERTIME' | 'ABSENT' | 'ON_LEAVE';
  remarks: string;
}

interface AttendanceModalProps {
  record: AttendanceRecord | null;
  employees: Employee[];
  onClose: () => void;
  onSave: (record: AttendanceRecord) => void;
}

const AttendanceModal: React.FC<AttendanceModalProps> = ({ record, employees, onClose, onSave }) => {
  const [formData, setFormData] = useState<AttendanceRecord>(
    record || {
      recordId: 0,
      employeeCode: '',
      employeeName: '',
      date: new Date().toISOString().split('T')[0],
      timeIn: '',
      timeOut: '',
      regularHours: 0,
      overtimeHours: 0,
      lateMinutes: 0,
      undertimeMinutes: 0,
      leaveType: 'NONE',
      status: 'PRESENT',
      remarks: ''
    }
  );

  const handleEmployeeChange = (employeeCode: string) => {
    const selectedEmployee = employees.find(e => e.employeeCode === employeeCode);
    if (selectedEmployee) {
      setFormData({
        ...formData,
        employeeCode: selectedEmployee.employeeCode,
        employeeName: selectedEmployee.fullName
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{record ? 'Edit Attendance Record' : 'Add Attendance Record'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Employee *</label>
                <select
                  required
                  value={formData.employeeCode}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  disabled={!!record}
                >
                  <option value="">Select Employee</option>
                  {employees.filter(e => e.isActive === 'Y').map(employee => (
                    <option key={employee.employeeId} value={employee.employeeCode}>
                      {employee.employeeCode} - {employee.fullName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Time In</label>
                <input
                  type="time"
                  value={formData.timeIn}
                  onChange={(e) => setFormData({ ...formData, timeIn: e.target.value })}
                  disabled={formData.status === 'ON_LEAVE' || formData.status === 'ABSENT'}
                />
              </div>
              <div className="form-group">
                <label>Time Out</label>
                <input
                  type="time"
                  value={formData.timeOut}
                  onChange={(e) => setFormData({ ...formData, timeOut: e.target.value })}
                  disabled={formData.status === 'ON_LEAVE' || formData.status === 'ABSENT'}
                />
              </div>
            </div>

            <div className="form-row form-row-4">
              <div className="form-group">
                <label>Regular Hours</label>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  max="12"
                  value={formData.regularHours}
                  onChange={(e) => setFormData({ ...formData, regularHours: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>Overtime Hours</label>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  max="12"
                  value={formData.overtimeHours}
                  onChange={(e) => setFormData({ ...formData, overtimeHours: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>Late (minutes)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.lateMinutes}
                  onChange={(e) => setFormData({ ...formData, lateMinutes: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>Undertime (minutes)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.undertimeMinutes}
                  onChange={(e) => setFormData({ ...formData, undertimeMinutes: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Status *</label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="PRESENT">Present</option>
                  <option value="LATE">Late</option>
                  <option value="UNDERTIME">Undertime</option>
                  <option value="ABSENT">Absent</option>
                  <option value="ON_LEAVE">On Leave</option>
                </select>
              </div>
              <div className="form-group">
                <label>Leave Type</label>
                <select
                  value={formData.leaveType}
                  onChange={(e) => setFormData({ ...formData, leaveType: e.target.value as any })}
                >
                  <option value="NONE">None</option>
                  <option value="SICK_LEAVE">Sick Leave</option>
                  <option value="VACATION_LEAVE">Vacation Leave</option>
                  <option value="EMERGENCY_LEAVE">Emergency Leave</option>
                  <option value="ABSENT">Absent</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={3}
                placeholder="Additional notes about this attendance record..."
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {record ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Timekeeping: React.FC = () => {
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<AttendanceRecord | null>(null);

  useEffect(() => {
    // TODO: Fetch employees from API
    setEmployees([
      {
        employeeId: 1,
        employeeCode: 'EMP001',
        firstName: 'Juan',
        middleName: 'Santos',
        lastName: 'Dela Cruz',
        fullName: 'Juan Dela Cruz',
        department: 'Production',
        position: 'Production Manager',
        tin: '123-456-789-000',
        sssNumber: '12-3456789-0',
        philhealthNumber: '12-345678901-2',
        pagibigNumber: '1234-5678-9012',
        basicSalary: 35000,
        email: 'juan.delacruz@dewfoam.ph',
        mobile: '+63 917 123 4567',
        isActive: 'Y'
      },
      {
        employeeId: 2,
        employeeCode: 'EMP002',
        firstName: 'Maria',
        middleName: 'Garcia',
        lastName: 'Santos',
        fullName: 'Maria Santos',
        department: 'Accounting',
        position: 'Accountant',
        tin: '234-567-890-000',
        sssNumber: '23-4567890-1',
        philhealthNumber: '23-456789012-3',
        pagibigNumber: '2345-6789-0123',
        basicSalary: 30000,
        email: 'maria.santos@dewfoam.ph',
        mobile: '+63 917 234 5678',
        isActive: 'Y'
      },
      {
        employeeId: 3,
        employeeCode: 'EMP003',
        firstName: 'Pedro',
        middleName: 'Reyes',
        lastName: 'Garcia',
        fullName: 'Pedro Reyes',
        department: 'Sales',
        position: 'Sales Executive',
        tin: '345-678-901-000',
        sssNumber: '34-5678901-2',
        philhealthNumber: '34-567890123-4',
        pagibigNumber: '3456-7890-1234',
        basicSalary: 25000,
        email: 'pedro.garcia@dewfoam.ph',
        mobile: '+63 917 345 6789',
        isActive: 'Y'
      },
      {
        employeeId: 4,
        employeeCode: 'EMP004',
        firstName: 'Ana',
        middleName: 'Reyes',
        lastName: 'Garcia',
        fullName: 'Ana Garcia',
        department: 'HR',
        position: 'HR Manager',
        tin: '456-789-012-000',
        sssNumber: '45-6789012-3',
        philhealthNumber: '45-678901234-5',
        pagibigNumber: '4567-8901-2345',
        basicSalary: 32000,
        email: 'ana.garcia@dewfoam.ph',
        mobile: '+63 917 456 7890',
        isActive: 'Y'
      },
      {
        employeeId: 5,
        employeeCode: 'EMP005',
        firstName: 'Roberto',
        middleName: 'Cruz',
        lastName: 'Tan',
        fullName: 'Roberto Tan',
        department: 'Production',
        position: 'Production Worker',
        tin: '567-890-123-000',
        sssNumber: '56-7890123-4',
        philhealthNumber: '56-789012345-6',
        pagibigNumber: '5678-9012-3456',
        basicSalary: 18000,
        email: 'roberto.tan@dewfoam.ph',
        mobile: '+63 917 567 8901',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch attendance records from API
    setAttendanceRecords([
      {
        recordId: 1,
        employeeCode: 'EMP001',
        employeeName: 'Juan Dela Cruz',
        date: '2025-03-05',
        timeIn: '08:00',
        timeOut: '17:00',
        regularHours: 8,
        overtimeHours: 0,
        lateMinutes: 0,
        undertimeMinutes: 0,
        leaveType: 'NONE',
        status: 'PRESENT',
        remarks: ''
      },
      {
        recordId: 2,
        employeeCode: 'EMP002',
        employeeName: 'Maria Santos',
        date: '2025-03-05',
        timeIn: '08:15',
        timeOut: '19:00',
        regularHours: 8,
        overtimeHours: 2.75,
        lateMinutes: 15,
        undertimeMinutes: 0,
        leaveType: 'NONE',
        status: 'LATE',
        remarks: 'Late arrival - traffic'
      },
      {
        recordId: 3,
        employeeCode: 'EMP003',
        employeeName: 'Pedro Reyes',
        date: '2025-03-05',
        timeIn: '08:00',
        timeOut: '20:30',
        regularHours: 8,
        overtimeHours: 4.5,
        lateMinutes: 0,
        undertimeMinutes: 0,
        leaveType: 'NONE',
        status: 'PRESENT',
        remarks: 'Overtime for urgent order'
      },
      {
        recordId: 4,
        employeeCode: 'EMP004',
        employeeName: 'Ana Garcia',
        date: '2025-03-05',
        timeIn: '',
        timeOut: '',
        regularHours: 0,
        overtimeHours: 0,
        lateMinutes: 0,
        undertimeMinutes: 0,
        leaveType: 'SICK_LEAVE',
        status: 'ON_LEAVE',
        remarks: 'Medical certificate submitted'
      },
      {
        recordId: 5,
        employeeCode: 'EMP005',
        employeeName: 'Roberto Tan',
        date: '2025-03-05',
        timeIn: '08:00',
        timeOut: '16:00',
        regularHours: 7,
        overtimeHours: 0,
        lateMinutes: 0,
        undertimeMinutes: 60,
        leaveType: 'NONE',
        status: 'UNDERTIME',
        remarks: 'Personal emergency'
      }
    ]);
  }, [selectedDate]);

  const filteredRecords = attendanceRecords.filter(record =>
    record.employeeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.employeeName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingRecord(null);
    setShowModal(true);
  };

  const handleEdit = (record: AttendanceRecord) => {
    setEditingRecord(record);
    setShowModal(true);
  };

  const handleExport = () => {
    alert('Export attendance report functionality would be implemented here');
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PRESENT': return 'badge-success';
      case 'LATE': return 'badge-warning';
      case 'UNDERTIME': return 'badge-info';
      case 'ABSENT': return 'badge-danger';
      case 'ON_LEAVE': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const summary = {
    totalEmployees: attendanceRecords.length,
    present: attendanceRecords.filter(r => r.status === 'PRESENT' || r.status === 'LATE' || r.status === 'UNDERTIME').length,
    absent: attendanceRecords.filter(r => r.status === 'ABSENT').length,
    onLeave: attendanceRecords.filter(r => r.status === 'ON_LEAVE').length,
    totalOvertimeHours: attendanceRecords.reduce((sum, r) => sum + r.overtimeHours, 0)
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Timekeeping & Attendance</h2>
            <p className="subtitle">Track employee attendance, overtime, and leave records</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handleExport}>
              <MdFileDownload /> Export Report
            </button>
            <button className="btn btn-primary" onClick={handleAdd}>
              <MdAdd /> Add Attendance
            </button>
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">👥</div>
            <div className="summary-content">
              <p className="summary-label">Total Employees</p>
              <p className="summary-value">{summary.totalEmployees}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">✅</div>
            <div className="summary-content">
              <p className="summary-label">Present</p>
              <p className="summary-value" style={{ color: '#28a745' }}>{summary.present}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📋</div>
            <div className="summary-content">
              <p className="summary-label">On Leave</p>
              <p className="summary-value" style={{ color: '#6c757d' }}>{summary.onLeave}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">⏰</div>
            <div className="summary-content">
              <p className="summary-label">Total OT Hours</p>
              <p className="summary-value" style={{ color: '#17a2b8' }}>{summary.totalOvertimeHours.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginLeft: 'auto', minWidth: '200px' }}>
              <label>Date</label>
              <input
                type="date"
                  lang="en-US"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee Code</th>
                  <th>Employee Name</th>
                  <th>Time In</th>
                  <th>Time Out</th>
                  <th>Regular Hours</th>
                  <th>OT Hours</th>
                  <th>Late (min)</th>
                  <th>Undertime (min)</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map(record => (
                  <tr key={record.recordId}>
                    <td className="code">{record.employeeCode}</td>
                    <td className="name">{record.employeeName}</td>
                    <td style={{ textAlign: 'center' }}>{record.timeIn || '-'}</td>
                    <td style={{ textAlign: 'center' }}>{record.timeOut || '-'}</td>
                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      {record.regularHours > 0 ? `${record.regularHours}h` : '-'}
                    </td>
                    <td style={{ textAlign: 'center', color: record.overtimeHours > 0 ? '#17a2b8' : 'inherit', fontWeight: 'bold' }}>
                      {record.overtimeHours > 0 ? `${record.overtimeHours}h` : '-'}
                    </td>
                    <td style={{ textAlign: 'center', color: record.lateMinutes > 0 ? '#ffc107' : 'inherit' }}>
                      {record.lateMinutes > 0 ? record.lateMinutes : '-'}
                    </td>
                    <td style={{ textAlign: 'center', color: record.undertimeMinutes > 0 ? '#dc3545' : 'inherit' }}>
                      {record.undertimeMinutes > 0 ? record.undertimeMinutes : '-'}
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(record.status)}`}>
                        {record.status.replace('_', ' ')}
                      </span>
                      {record.leaveType !== 'NONE' && (
                        <div style={{ fontSize: '11px', marginTop: '4px' }}>
                          ({record.leaveType.replace('_', ' ')})
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(record)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredRecords.length === 0 && (
              <div className="no-data">
                <p>No attendance records found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <AttendanceModal
          record={editingRecord}
          employees={employees}
          onClose={() => setShowModal(false)}
          onSave={(record) => {
            if (editingRecord) {
              setAttendanceRecords(attendanceRecords.map(r =>
                r.recordId === record.recordId ? record : r
              ));
            } else {
              setAttendanceRecords([...attendanceRecords, { ...record, recordId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

export default Timekeeping;
